// BOT-EDIT: 2025-01-27T10:35:00Z Add invoice management and enhanced card actions to DashboardPage
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { CardService, CardData } from '../services/cardService';
import { UserService } from '../services/userService';
import { QRService } from '../services/qrService';
import QRCode from '../components/QRCode';
import { downloadQRPng, downloadQRSvg } from '../utils/qrDownload';


const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading, initializing } = useAuth();
  const { userPlan, isPro, isStandard, isCardOnly, isTrialActive, isExpired, trialEndsAt, planExpiresAt, isLoading: subscriptionLoading, refreshSubscription, planLimits, canCreateCard, canCreateInvoice, cardCount, invoiceCount, monthlyInvoiceCount } = useSubscription();
  

  // 구독 정보 디버깅
  useEffect(() => {
    console.log('🔍 [DashboardPage] Subscription info debug:', {
      userPlan,
      isPro,
      isStandard,
      isCardOnly,
      isTrialActive,
      isExpired,
      trialEndsAt: trialEndsAt?.toISOString(),
      planExpiresAt: planExpiresAt?.toISOString(),
      subscriptionLoading,
      canCreateCard,
      canCreateInvoice
    });
  }, [userPlan, isPro, isStandard, isCardOnly, isTrialActive, isExpired, trialEndsAt, planExpiresAt, subscriptionLoading, canCreateCard, canCreateInvoice]);


  // 🆕 결제 성공 시 구독 상태 새로고침 (대시보드에서도)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success') === 'true';
    const sessionId = urlParams.get('session_id');
    
    if (paymentSuccess || sessionId) {
      console.log('🔄 [DashboardPage] Payment success detected, scheduling subscription refresh');
      
      // 5초 후 구독 상태 새로고침 (웹훅 처리 시간 충분히 고려)
      const refreshTimer = setTimeout(() => {
        console.log('🔄 [DashboardPage] Executing subscription refresh');
        refreshSubscription();
      }, 5000);
      
      return () => clearTimeout(refreshTimer);
    }
  }, [refreshSubscription]);


  const [qrOpen, setQrOpen] = useState(false);
  const [qrValue, setQrValue] = useState('');
  const [qrDesign, setQrDesign] = useState<any>(null);
  const [qrDownloading, setQrDownloading] = useState<'png' | 'svg' | null>(null);
  const [selectedCard, setSelectedCard] = useState<CardData | null>(null);

  const [userCards, setUserCards] = useState<CardData[]>([]);
  const [loading, setLoading] = useState(true);

  // 🆕 구독 제한 알림 상태
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [limitAlertType, setLimitAlertType] = useState<'card' | 'invoice' | 'expired' | null>(null);

  // 🆕 구독 제한 체크 및 알림 표시
  useEffect(() => {
    if (subscriptionLoading) return;

    // 구독 만료 체크
    if (isExpired) {
      setLimitAlertType('expired');
      setShowLimitAlert(true);
      return;
    }

    // 명함 생성 제한 체크
    if (!canCreateCard && cardCount >= (planLimits?.maxCards || 0)) {
      setLimitAlertType('card');
      setShowLimitAlert(true);
      return;
    }

    // 인보이스 생성 제한 체크 (카드 전용 플랜 제외)
    if (!isCardOnly && !canCreateInvoice) {
      setLimitAlertType('invoice');
      setShowLimitAlert(true);
      return;
    }

    // 제한 없으면 알림 숨기기
    setShowLimitAlert(false);
    setLimitAlertType(null);
  }, [subscriptionLoading, cardCount, isExpired, canCreateCard, canCreateInvoice, isCardOnly, planLimits]);

  // 인보이스 관리 모달 상태
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedCardForInvoice, setSelectedCardForInvoice] = useState<CardData | null>(null);
  

  // 안전한 번역 함수
  const safeT = (key: string, options: any = {}) => {
    try {
      const tOptions = typeof options === 'object' && options !== null ? options : {};
      const current = t(key, tOptions);
      if (typeof current === 'string' && current !== key) return current;

      const en = t(key, { ...tOptions, lng: 'en' });
      if (typeof en === 'string' && en !== key) return en;

      return key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };




  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.uid) {
        console.log('⚠️ No user UID available, waiting for auth...');
        return;
      }
      
      try {
        setLoading(true);
        console.log('🔄 Loading dashboard data for user:', user.uid);
        
        // 새로고침 대응: 잠시 대기
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // getOrCreateUserProfile 사용하여 프로필 보장
        const profile = await UserService.getOrCreateUserProfile(user.uid, {
          email: user.email || '',
          displayName: user.displayName || undefined,
          photoURL: user.photoURL || undefined
        });
        
        console.log('✅ Profile loaded/created:', profile);
        
        // 카드 데이터 로드
        const cards = await CardService.getUserCards(user.uid);
        console.log('✅ Cards loaded:', cards.length);
        setUserCards(cards);
        
      } catch (error) {
        console.error('❌ Dashboard data load failed:', error);
      } finally {
        setLoading(false);
      }
    };

    // 인증 상태가 완전히 로드된 후에만 데이터 로드
    if (user?.uid) {
      console.log('�� User authenticated, loading dashboard data...');
      loadDashboardData();
    } else {
      console.log('⏳ Waiting for user authentication...');
      setLoading(true);
    }
  }, [user?.uid]);




  const handleQRView = async (card: CardData) => {
    try {
      setSelectedCard(card);
      
      // QR 코드 URL 설정 - following app logic
      let url = '';
      if (card.customPath) {
        url = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        url = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        url = card.shareUrl;
      } else {
        url = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      setQrValue(url);
      
      // 기존 QR 디자인 데이터 마이그레이션
      await QRService.migrateQRDesignData(user!.uid, card.id!);
      
      // 저장된 QR 디자인 불러오기
      const savedQRDesign = await QRService.getQRDesign(user!.uid, card.id!);
      setQrDesign(savedQRDesign);

      // QR 디자인 페이지에서 사용자가 직접 수정한 주소가 있으면 그 주소 사용
      // (과거 자동 생성 주소는 무시하고 프로필 주소 유지)
      const legacyAutoUrls = [
        `https://scanvcf.com/qr/${card.id}`,
        (card as any).customUrl ? `https://scanvcf.com/u/${(card as any).customUrl}` : null,
        `https://scanvcf.com/card/${user!.uid}/${card.id}`
      ].filter(Boolean);
      if (savedQRDesign?.qrValue && !legacyAutoUrls.includes(savedQRDesign.qrValue)) {
        setQrValue(savedQRDesign.qrValue);
      }

      setQrOpen(true);
    } catch (error) {
      console.error('Failed to load QR design:', error);
      // 기본 QR 코드로 표시 - following app logic
      let url = '';
      if (card.customPath) {
        url = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        url = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        url = card.shareUrl;
      } else {
        url = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      setQrValue(url);
      setQrDesign(null);
      setQrOpen(true);
    }
  };

  // 인보이스 관리 모달 열기
  const handleInvoiceManagement = (card: CardData) => {
    // ✅ 모든 플랜에서 인보이스 관리 가능 (추후 제한 필요시 구독 컨텍스트에서 처리)
    setSelectedCardForInvoice(card);
    setShowInvoiceModal(true);
  };

  // 인보이스 관리 옵션 선택
  const handleInvoiceOption = (option: string, card: CardData) => {
    setShowInvoiceModal(false);
    setSelectedCardForInvoice(null);
    
    switch (option) {
      case 'create':
        navigate('/invoice/create', { 
          state: { cardData: card, cardLanguage: 'ko' }
        });
        break;
      case 'list':
        navigate('/invoices', { 
          state: { cardId: card.id, cardData: card, cardLanguage: 'ko' }
        });
        break;
      case 'settings':
        navigate('/business-profile', { 
          state: { cardId: card.id, cardLanguage: 'ko' }
        });
        break;
    }
  };


  // 카드 공유
  const handleShareCard = async (card: CardData) => {
    try {
      let shareUrl = '';
      if (card.customPath) {
        shareUrl = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        shareUrl = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        shareUrl = card.shareUrl;
      } else {
        shareUrl = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      
      // 클립보드에 URL 복사
      await navigator.clipboard.writeText(shareUrl);
      
      // 성공 메시지 표시
      alert('링크가 복사되었습니다');
      
    } catch (error) {
      console.error('Error sharing card:', error);
      alert('공유 중 오류가 발생했습니다');
    }
  };

  // 카드 링크 복사
  const handleCopyLink = async (card: CardData) => {
    try {
      let shareUrl = '';
      if (card.customPath) {
        shareUrl = `https://scanvcf.com/${card.customPath}`;
      } else if (card.customDomain) {
        shareUrl = `https://${card.customDomain}`;
      } else if (card.shareUrl) {
        shareUrl = card.shareUrl;
      } else {
        shareUrl = `https://scanvcf.com/card/${user!.uid}/${card.id}`;
      }
      
      await navigator.clipboard.writeText(shareUrl);
      
      // URL 복사 이벤트를 Cloud Function에 전송
      const logEventUrl = 'https://us-central1-scanvcf-375fb.cloudfunctions.net/logEvent';
      const response = await fetch(logEventUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cardId: card.id,
          eventType: 'urlCopy',
          userId: user!.uid,
          timestamp: new Date().toISOString()
        })
      });

      console.log('◆◆ [DashboardPage] URL 복사 이벤트 응답:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      alert(safeT('functions.share', '링크가 복사되었습니다'));
    } catch (error) {
      console.error('Error copying link:', error);
      alert(safeT('common.error', '링크 복사 중 오류가 발생했습니다'));
    }
  };

  // 카드 삭제
  const handleDeleteCard = async (cardId: string, cardName: string) => {
    const deleteTitle = safeT('home.deleteConfirmTitle', { defaultValue: 'Delete Card' });
    const deleteMessage = safeT('home.deleteConfirmMessage', {
      cardName,
      defaultValue: `Do you want to delete ${cardName} card?`,
    });
    const confirmDelete = window.confirm(
      `${deleteTitle}\n${deleteMessage}`
    );
    
    if (confirmDelete) {
      try {
        await CardService.deleteCard(user!.uid, cardId);
        // 카드 목록 새로고침
        const cards = await CardService.getUserCards(user!.uid);
        setUserCards(cards);
        alert(safeT('home.deleteSuccessMessage', { defaultValue: 'Card has been successfully deleted' }));
      } catch (error) {
        console.error('Error deleting card:', error);
        alert(safeT('home.errors.deleteCard', { defaultValue: 'Error occurred while deleting card' }));
      }
    }
  };


  // 초기화 중이거나 인증 로딩 중이면 로딩 표시
  if (initializing || authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {t('common.loading', 'Loading...')}
          </p>
        </div>
      </div>
    );
  }

  // 초기화와 로딩이 완료되었지만 사용자가 없으면 로그인 페이지로 리다이렉트
  if (!initializing && !authLoading && !user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('common.loading', 'Login required.')}</p>
          <button
            onClick={() => navigate('/login')}
            className="lux-btn-gold px-5 py-2.5 rounded-full font-semibold"
          >
            {t('common.login', 'Login')}
          </button>
        </div>
      </div>
    );
  }

  // 구독 정보 로딩 중이면 로딩 표시
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {t('common.loading', 'Loading subscription data...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lux-ivory">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 pt-20 md:pt-6">
        <div className="px-4 py-6 sm:px-0">
          {/* 구독 상태 배너 - 모바일 최적화 */}
          {(isExpired || isTrialActive || isCardOnly || isStandard || isPro) && (
            <div className="mb-6 p-5 lux-dark-section border border-[#c8a45e]/25 rounded-2xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1">
                  <div className={`font-bold text-lg mb-3 tracking-tight ${isExpired ? 'text-red-400' : 'text-[#dcc08a]'}`}>
                    {isExpired 
                      ? t('subscription.expired.title')
                      : isTrialActive 
                        ? t('subscription.plans.freeTrialName')
                        : isCardOnly
                          ? t('subscription.plans.cardOnly')
                          : isStandard
                            ? t('subscription.plans.standard')
                            : isPro
                              ? t('subscription.plans.pro')
                              : t('subscription.currentStatus')
                    }
                  </div>
                  
                  {/* 구독 상태 정보 - 모바일 최적화 */}
                  <div className="space-y-3">
                    {/* 사용량 정보 - 모바일에서 세로 배치 */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center justify-between bg-white/5 p-3 rounded-lg border border-white/10">
                        <span className="text-white/70 font-medium">{t('subscription.businessCard')}</span>
                        <span className="text-white font-bold">{cardCount}/{planLimits.maxCards === -1 ? '∞' : planLimits.maxCards}</span>
                      </div>
                      <div className="flex items-center justify-between bg-white/5 p-3 rounded-lg border border-white/10">
                        <span className="text-white/70 font-medium">{t('invoice.title')}</span>
                        <span className="text-white font-bold">{invoiceCount}/{planLimits.maxInvoicesPerYear === -1 ? '∞' : planLimits.maxInvoicesPerYear}</span>
                      </div>
                      {monthlyInvoiceCount > 0 && (
                        <div className="flex items-center justify-between bg-white/5 p-3 rounded-lg border border-white/10">
                          <span className="text-white/70 font-medium">{t('subscription.monthly')}</span>
                          <span className="text-white font-bold">{monthlyInvoiceCount}/{planLimits.maxInvoicesPerMonth === -1 ? '∞' : planLimits.maxInvoicesPerMonth}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* 만료일 정보 */}
                    {(trialEndsAt || planExpiresAt) && (
                      <div className="text-sm bg-white/5 p-3 rounded-lg border border-white/10">
                        {isTrialActive && trialEndsAt && (
                          <div className="flex items-center justify-between">
                            <span className="text-white/60">{t('subscription.trialEndsAt')}:</span>
                            <span className="font-medium text-white">{trialEndsAt.toLocaleDateString()}</span>
                          </div>
                        )}
                        {!isTrialActive && planExpiresAt && (
                          <div className="flex items-center justify-between">
                            <span className="text-white/60">{t('subscription.planExpiresAt')}:</span>
                            <span className="font-medium text-white">{planExpiresAt.toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* 플랜 기능 정보 - 모바일에서 세로 배치 */}
                    <div className="text-sm text-white/80">
                      <div className="flex flex-wrap gap-2">
                        {planLimits.customDomain && (
                          <span className="flex items-center space-x-1 bg-[#c8a45e]/15 border border-[#c8a45e]/30 px-2.5 py-1 rounded-full">
                            <svg className="w-3 h-3 text-[#dcc08a]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs">{t('subscription.features.customDomain', '커스텀 도메인')}</span>
                          </span>
                        )}
                        {planLimits.qrLogo && (
                          <span className="flex items-center space-x-1 bg-[#c8a45e]/15 border border-[#c8a45e]/30 px-2.5 py-1 rounded-full">
                            <svg className="w-3 h-3 text-[#dcc08a]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs">{t('subscription.features.qrLogo', 'QR 로고')}</span>
                          </span>
                        )}
                        {(planLimits as any).statistics && (
                          <span className="flex items-center space-x-1 bg-[#c8a45e]/15 border border-[#c8a45e]/30 px-2.5 py-1 rounded-full">
                            <svg className="w-3 h-3 text-[#dcc08a]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs">{t('subscription.features.statistics', '통계')}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* 버튼들 - 모바일에서 세로 배치 */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-4 sm:mt-0 sm:ml-4">
                  <button
                    onClick={() => navigate('/subscription-management')}
                    className="lux-btn-ghost px-4 py-2 text-sm font-semibold rounded-full"
                  >
                    {t('subscription.manage')}
                  </button>
                  <button
                    onClick={() => navigate('/paywall')}
                    className="lux-btn-gold px-4 py-2 text-sm font-semibold rounded-full"
                  >
                    {t('subscription.upgrade')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 🆕 구독 제한 알림 */}
          {showLimitAlert && limitAlertType && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-red-800">
                    {limitAlertType === 'expired' && t('subscription.expired.title')}
                    {limitAlertType === 'card' && t('subscription.cardLimitReached')}
                    {limitAlertType === 'invoice' && t('subscription.invoiceLimitReached')}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {limitAlertType === 'expired' && (
                      <p>
                        {t('subscription.expired.message')}
                        {/* 디버깅: 번역 키 확인 */}
                        {process.env.NODE_ENV === 'development' && (
                          <span className="text-xs text-gray-400 ml-2">
                            [Key: subscription.expired.message]
                          </span>
                        )}
                      </p>
                    )}
                    {limitAlertType === 'card' && (
                      <p>{t('subscription.cardLimitMessage', { maxCards: planLimits?.maxCards || 0 })}</p>
                    )}
                    {limitAlertType === 'invoice' && (
                      <p>{t('subscription.invoiceLimitMessage')}</p>
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="-mx-2 -my-1.5 flex">
                      <button
                        onClick={() => navigate('/paywall')}
                        className="bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                      >
                        {t('subscription.upgrade')}
                      </button>
                      <button
                        onClick={() => setShowLimitAlert(false)}
                        className="ml-3 bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                      >
                        {t('invoiceManagement.close')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 카드 목록 - 모바일 최적화 */}
          <div className="bg-white overflow-hidden border border-[#e8e6e1] shadow-sm rounded-2xl">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t('navigation.myCards')} ({cardCount})
                </h3>
                <button
                  onClick={() => {
                    if (!canCreateCard) {
                      setLimitAlertType('card');
                      setShowLimitAlert(true);
                      return;
                    }
                    navigate('/card/create');
                  }}
                  className={`w-full sm:w-auto px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 flex items-center justify-center ${
                    canCreateCard
                      ? 'lux-btn-gold'
                      : 'bg-gray-400 text-white cursor-not-allowed'
                  }`}
                  disabled={!canCreateCard}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  + {t('nav.tab.addCard')}
                </button>
              </div>
              
              {userCards.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t('common.noData', 'No cards created yet.')}
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {userCards.map((card) => (
                    <div key={card.id} className="bg-white border border-[#e8e6e1] rounded-xl p-4 hover:shadow-lg transition-all duration-200 hover:border-[#c8a45e]/50">
                      {/* 카드 헤더 - 모바일 최적화 */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                            {card.photoUrl ? (
                              <img src={card.photoUrl} alt="Profile" className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 truncate">{card.name}</h4>
                            <p className="text-sm text-gray-600 truncate">{card.position}</p>
                          </div>
                        </div>
                        
                        {/* 퀵 액션 버튼 - 모바일에서 더 작게 */}
                        <div className="flex space-x-1 flex-shrink-0">
                          <button
                            onClick={() => handleQRView(card)}
                            className="p-2 text-gray-500 hover:text-[#96742f] hover:bg-[#c8a45e]/10 rounded-lg transition-all duration-200"
                            title={safeT('functions.share', 'QR 코드 보기')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCopyLink(card)}
                            className="p-2 text-gray-500 hover:text-[#96742f] hover:bg-[#c8a45e]/10 rounded-lg transition-all duration-200"
                            title={safeT('functions.share', '링크 복사')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleShareCard(card)}
                            className="p-2 text-gray-500 hover:text-[#96742f] hover:bg-[#c8a45e]/10 rounded-lg transition-all duration-200"
                            title={safeT('functions.share', '공유')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              if (!isPro) {
                                navigate('/paywall', { 
                                  state: { limitType: 'statistics' }
                                });
                                return;
                              }
                              navigate(`/stats/${card.id}`);
                            }}
                            className={`p-2 rounded-lg transition-all duration-200 ${
                              isPro 
                                ? 'text-gray-500 hover:text-[#96742f] hover:bg-[#c8a45e]/10'
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                            title={isPro ? safeT('functions.share', '통계 보기') : safeT('subscription.statisticsLimit', '프로 플랜 필요')}
                            disabled={!isPro}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-1">{card.email}</p>
                      <p className="text-sm text-gray-600 mb-1">{card.company}</p>

                      {/* URL Display - following app logic */}
                      {(() => {
                        let displayUrl = '';
                        if (card.customPath) {
                          displayUrl = `https://scanvcf.com/${card.customPath}`;
                        } else if (card.customDomain) {
                          displayUrl = `https://${card.customDomain}`;
                        } else if (card.shareUrl) {
                          displayUrl = card.shareUrl;
                        } else {
                          displayUrl = 'no-url';
                        }
                        
                        return displayUrl !== 'no-url' ? (
                          <div className="mt-3 p-3 lux-ivory rounded-lg border border-[#c8a45e]/30">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4 text-[#96742f]" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm text-[#96742f] font-medium">{t('common.website', 'Website')}:</span>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(displayUrl);
                                    
                                    // URL 복사 이벤트를 Cloud Function에 전송
                                    const logEventUrl = 'https://us-central1-scanvcf-375fb.cloudfunctions.net/logEvent';
                                    const response = await fetch(logEventUrl, {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                      },
                                      body: JSON.stringify({
                                        cardId: card.id,
                                        eventType: 'urlCopy',
                                        userId: user!.uid,
                                        timestamp: new Date().toISOString()
                                      })
                                    });

                                    console.log('◆◆ [DashboardPage] URL 복사 이벤트 응답:', {
                                      status: response.status,
                                      statusText: response.statusText,
                                      ok: response.ok
                                    });

                                    alert(t('qr.copied', '링크가 복사되었습니다'));
                                  } catch (error) {
                                    console.error('Error copying URL:', error);
                                    alert(t('functions.share', '링크가 복사되었습니다'));
                                  }
                                }}
                                className="p-1.5 text-[#96742f] hover:text-[#7a5d24] hover:bg-[#c8a45e]/15 rounded-md transition-all duration-200"
                                title={t('functions.share', '링크 복사')}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                            </div>
                            <a
                              href={displayUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-sm text-gray-700 hover:text-[#96742f] underline decoration-[#c8a45e]/40 underline-offset-4 mt-2 break-all transition-colors cursor-pointer font-medium"
                            >
                              {displayUrl}
                            </a>
                          </div>
                        ) : null;
                      })()}

                      {/* 메인 액션 버튼 - 모바일 최적화 */}
                      <div className="mt-4 flex space-x-2">
                        <button
                          onClick={() => handleInvoiceManagement(card)}
                          className="flex-1 flex items-center justify-center px-3 py-3 lux-ink text-white text-sm rounded-lg hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {t('invoice.title', 'invoice')}
                        </button>
                        
                        <button
                          onClick={() => navigate(`/cards/${card.id}/edit`)}
                          className="flex-1 flex items-center justify-center px-3 py-3 lux-ink text-white text-sm rounded-lg hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          {t('home.actions.edit', t('common.edit', 'Edit'))}
                        </button>
                      </div>

                      {/* 추가 액션 - 항상 표시 */}
                      <div className="mt-3 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => handleShareCard(card)}
                              className="flex items-center justify-center px-3 py-3 lux-ink text-white text-sm rounded-lg hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                              </svg>
                              <span className="whitespace-nowrap">{t('common.share', 'Share')}</span>
                            </button>
                            
                            <button
                              onClick={() => navigate(`/cards/${card.id}/qr-design`)}
                              className="flex items-center justify-center px-3 py-3 lux-ink text-white text-sm rounded-lg hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                              </svg>
                              {t('cardEdit.qrDesign', 'QR Design')}
                            </button>
                            
                            <button
                              onClick={() => {
                                if (!isPro) {
                                  navigate('/paywall', { 
                                    state: { limitType: 'statistics' }
                                  });
                                  return;
                                }
                                navigate(`/stats/${card.id}`);
                              }}
                              className={`flex items-center justify-center px-3 py-3 text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${
                                isPro
                                  ? 'lux-ink text-white hover:opacity-90'
                                  : 'bg-gray-400 text-white cursor-not-allowed'
                              }`}
                              disabled={!isPro}
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              {t('stats.title', '통계')}
                            </button>
                            
                            <button
                              onClick={() => handleDeleteCard(card.id!, card.name)}
                              className="flex items-center justify-center px-3 py-3 bg-red-600 text-white text-sm rounded-lg hover:bg-red-500 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              {safeT('home.deleteCard', { defaultValue: 'Delete Card' })}
                            </button>
                          </div>
                          
                          {/* 도메인 설정 버튼 - 프리미엄 전용 */}
                          {isPro && (
                            <div className="mt-3">
                              <button
                                onClick={() => navigate(`/domain-settings?cardId=${card.id}&initialPath=${card.customPath || ''}&initialDomain=${card.customDomain || ''}&isCustomDomain=false`)}
                                className="w-full flex items-center justify-center px-3 py-3 lux-ink text-white text-sm rounded-lg hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                                </svg>
                                {t('domainSettings.title', '도메인')}
                              </button>
                            </div>
                          )}
                          
                          {/* 프리미엄 전용 기능 안내 */}
                          {!isPro && (
                            <div className="mt-3">
                              <button
                                onClick={() => navigate('/paywall')}
                                className="w-full flex items-center justify-center px-3 py-3 bg-gray-400 text-white text-sm rounded-lg hover:bg-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                {t('domainSettings.customDomainTitle', { defaultValue: t('subscription.customDomainTitle', 'Custom Domain') })}
                              </button>
                            </div>
                          )}
                        </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* QR 모달 */}
        {qrOpen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 w-[320px] text-center">
              <h4 className="font-semibold mb-3">{t('functions.share', 'QR 코드')}</h4>

              {/* QR 코드 - 저장된 디자인 적용 */}
              <div className="flex justify-center mb-3">
                <QRCode
                  value={qrValue}
                  size={220}
                  color={qrDesign?.qrColor || '#000000'}
                  backgroundColor={qrDesign?.qrBackgroundColor || '#FFFFFF'}
                  logoUrl={qrDesign?.qrLogoUrl}
                  logoSize={60}
                  logoBackgroundColor="white"
                  logoMargin={6}
                  logoBorderRadius={6}
                  ecl="H"
                />
              </div>

              <p className="mt-3 text-xs break-all text-gray-600">{qrValue}</p>

              {/* 인쇄용 고해상도 다운로드 */}
              <div className="mt-4 flex gap-2 justify-center">
                <button
                  onClick={async () => {
                    if (qrDownloading) return;
                    setQrDownloading('png');
                    try {
                      const result = await downloadQRPng({
                        value: qrValue,
                        color: qrDesign?.qrColor || '#000000',
                        backgroundColor: qrDesign?.qrBackgroundColor || '#FFFFFF',
                        logoUrl: qrDesign?.qrLogoUrl,
                        filename: `${selectedCard?.name || 'scanvcf'}-qr`
                      });
                      if (qrDesign?.qrLogoUrl && !result.logoIncluded) {
                        alert(t('qrDesign.downloadLogoFailed', 'The logo could not be included. The QR code was downloaded without the logo.'));
                      }
                    } catch (error) {
                      console.error('Failed to download QR PNG:', error);
                      alert(t('qrDesign.downloadError', 'An error occurred while downloading the QR code.'));
                    } finally {
                      setQrDownloading(null);
                    }
                  }}
                  disabled={qrDownloading !== null}
                  className="flex-1 px-3 py-1.5 border rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  {qrDownloading === 'png' ? '...' : 'PNG'}
                </button>
                <button
                  onClick={async () => {
                    if (qrDownloading) return;
                    setQrDownloading('svg');
                    try {
                      const result = await downloadQRSvg({
                        value: qrValue,
                        color: qrDesign?.qrColor || '#000000',
                        backgroundColor: qrDesign?.qrBackgroundColor || '#FFFFFF',
                        logoUrl: qrDesign?.qrLogoUrl,
                        filename: `${selectedCard?.name || 'scanvcf'}-qr`
                      });
                      if (qrDesign?.qrLogoUrl && !result.logoIncluded) {
                        alert(t('qrDesign.downloadLogoFailed', 'The logo could not be included. The QR code was downloaded without the logo.'));
                      }
                    } catch (error) {
                      console.error('Failed to download QR SVG:', error);
                      alert(t('qrDesign.downloadError', 'An error occurred while downloading the QR code.'));
                    } finally {
                      setQrDownloading(null);
                    }
                  }}
                  disabled={qrDownloading !== null}
                  className="flex-1 px-3 py-1.5 border rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  {qrDownloading === 'svg' ? '...' : 'SVG'}
                </button>
              </div>

              <div className="mt-2 flex gap-2 justify-center">
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(qrValue);
                      
                      // URL 복사 이벤트를 Cloud Function에 전송
                      const logEventUrl = 'https://us-central1-scanvcf-375fb.cloudfunctions.net/logEvent';
                      const response = await fetch(logEventUrl, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          cardId: selectedCard?.id,
                          eventType: 'urlCopy',
                          userId: user!.uid,
                          timestamp: new Date().toISOString()
                        })
                      });

                      console.log('◆◆ [DashboardPage] QR URL 복사 이벤트 응답:', {
                        status: response.status,
                        statusText: response.statusText,
                        ok: response.ok
                      });

                      alert(t('functions.share', '링크가 복사되었습니다'));
                    } catch (error) {
                      console.error('Error copying QR URL:', error);
                      alert(t('functions.share', '링크가 복사되었습니다'));
                    }
                  }}
                  className="px-3 py-1 border rounded hover:bg-gray-50"
                >
                  {t('functions.share', '링크 복사')}
                </button>
                <button
                  onClick={() => setQrOpen(false)}
                  className="px-4 py-1.5 lux-ink text-white rounded-full hover:opacity-90 font-medium"
                >
                  {t('invoiceManagement.close')}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* 인보이스 관리 모달 */}
        {showInvoiceModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 w-[400px] max-w-[90vw]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {safeT('invoice.title')}
                </h3>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    if (!canCreateInvoice) {
                      setShowInvoiceModal(false);
                      setLimitAlertType('invoice');
                      setShowLimitAlert(true);
                      return;
                    }
                    handleInvoiceOption('create', selectedCardForInvoice!);
                  }}
                  className={`w-full flex items-center p-4 rounded-lg transition-colors ${
                    canCreateInvoice 
                      ? 'bg-[#c8a45e]/10 hover:bg-[#c8a45e]/20'
                      : 'bg-gray-100 cursor-not-allowed'
                  }`}
                  disabled={!canCreateInvoice}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                    canCreateInvoice ? 'bg-[#0a0e1a]' : 'bg-gray-400'
                  }`}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className={`font-medium ${canCreateInvoice ? 'text-gray-900' : 'text-gray-500'}`}>
                      {safeT('invoice.createNew')}
                    </div>
                    <div className={`text-sm ${canCreateInvoice ? 'text-gray-600' : 'text-gray-400'}`}>
                      {canCreateInvoice 
                        ? safeT('invoice.createNewDesc')
                        : safeT('subscription.invoiceLimitMessage')
                      }
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleInvoiceOption('list', selectedCardForInvoice!)}
                  className="w-full flex items-center p-4 bg-[#c8a45e]/10 hover:bg-[#c8a45e]/20 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-[#0a0e1a] rounded-full flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{safeT('invoice.list.title')}</div>
                    <div className="text-sm text-gray-600">{safeT('invoice.list.desc')}</div>
                  </div>
                </button>

                <button
                  onClick={() => handleInvoiceOption('settings', selectedCardForInvoice!)}
                  className="w-full flex items-center p-4 bg-[#c8a45e]/10 hover:bg-[#c8a45e]/20 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-[#0a0e1a] rounded-full flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{safeT('invoice.settings.title')}</div>
                    <div className="text-sm text-gray-600">{safeT('invoice.settings.settingsDesc')}</div>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setShowInvoiceModal(false)}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {safeT('invoiceManagement.close')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;